<template>
    <main-layout>
        <template #header>
            <main-header :title="'FAQ 게시물 작성' | translate"/>
        </template>
        <template #body>
            <main-body>
                <div class="d-flex mt-2">
                    <div class="ml-3">
                      <!-- 공지사항 타입 셀렉트 박스 -->
                      <search-single-select-box
                          :items="countryOptions"
                          :label="'국가코드' | translate"
                          @update:selected="onFilterCountryCodesChanged"
                          style="width: 150px;"
                          :value="countryCode"
                      />
                    </div>
                    <div class="ml-3">
                        <!-- 공지사항 타입 셀렉트 박스 -->
                        <search-single-select-box
                            :items="faqTypeOptions"
                            :label="'FAQ 타입' | translate"
                            @update:selected="onFilterNoticeTypeChanged"
                            style="width: 150px;"
                            :value="boardTypeId"
                        />
                    </div>
                </div>
                <v-text-field
                    class="faq-edit-field-tight"
                    :placeholder="'제목' | translate"
                    outlined
                    dense
                    v-model="title"
                    ref="title"
                />
                <v-combobox
                    class="faq-edit-field-tight faq-edit-tags"
                    :placeholder="'태그 문구별 Enter 키 입력 (예: #거래명세서, #이메일발송)' | translate"
                    outlined
                    dense
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    hide-selected
                    :items="[]"
                    v-model="tagItems"
                    :search-input.sync="tagInput"
                    @keydown.enter.prevent="commitTagInput"
                >
                    <template #selection="{ attrs, item, selected, disabled }">
                        <v-chip
                            v-bind="attrs"
                            :input-value="selected"
                            :disabled="disabled"
                            small
                            close
                            @click:close="removeTag(item)"
                        >
                            #{{ item }}
                        </v-chip>
                    </template>
                </v-combobox>
                <div class="faq-edit-body-split">
                    <div class="faq-edit-body-pane">
                        <quill-editor
                            ref="editor"
                            :options="options"
                            v-model="content"
                            class="faq-edit-quill"
                        />
                    </div>
                    <div class="faq-edit-body-pane">
                        <v-textarea
                            :placeholder="'content_plain (검색/요약용 텍스트)' | translate"
                            outlined
                            dense
                            no-resize
                            hide-details
                            v-model="contentPlain"
                            class="faq-edit-plain-textarea"
                        />
                    </div>
                </div>
            </main-body>
        </template>
        <template #footer>
            <div class="d-flex align-center">
                <v-btn @click="onSaveClick">
                    <v-icon>
                        mdi-content-save-outline
                    </v-icon>
                </v-btn>
                <v-btn
                    v-if="boardId"
                    class="ml-2"
                    color="error"
                    outlined
                    @click="onDeleteClick"
                >
                    <v-icon left>mdi-delete-outline</v-icon>
                    {{ '삭제' | translate }}
                </v-btn>
            </div>
        </template>
    </main-layout>
</template>

<script>
import MainHeader from "@/components/common/layout/MainHeader";
import MainLayout from "@/components/common/layout/MainLayout";
import MainBody from "@/components/common/layout/MainBody";
import SearchSingleSelectBox from "@/components/input/SearchSingleSelectBox";
import {DateTime} from "luxon";
import {PATH} from '@/constants'
import TotalCodeOptions from "@/assets/options/TotalCodeOptions";

export default {
    name: "FAQBoardEdit",
    components: {
        MainBody,
        MainLayout,
        MainHeader,
        SearchSingleSelectBox,
    },
    mixins: [TotalCodeOptions],
    created() {
        // 공지사항 옵션 로드
        this.loadCountryOptions();
        this.loadFAQTypeOptions()
        this.loadFAQDetailOptions()
        // 파라미터로 넘겨받은 값이 있는지 확인
        const raw = this.$route?.params?.raw

        if (typeof raw === "undefined") {
            // 기본 시간은 오늘로부터 1주일을 지정한다.
            const defaultStartDateTime = DateTime.local().plus({minutes: 15})
            const defaultEndDateTime = DateTime.local().plus({weeks: 1, minutes: 15})
            const startDate = defaultStartDateTime.toISODate()
            const startTime = defaultStartDateTime.toFormat('HH:mm')
            const endDate = defaultEndDateTime.toISODate()
            const endTime = defaultEndDateTime.toFormat('HH:mm')
            this.startDate = startDate
            this.startTime = startTime
            this.endDate = endDate
            this.endTime = endTime
        } else {
            this.boardId = raw.boardId
            this.countryCode = raw.countryCode
            this.boardTypeId = raw.boardTypeId
            this.noticeDetailYn = raw.noticeDetailYn
            this.title = raw.title
            this.content = raw.content
            this.contentPlain = raw.contentPlain || ''
            this.tagItems = this.parseTagNames(raw.tags || '')
            // 시간 처리
            const startDate = DateTime.fromSQL(raw.noticeStartDatetime).plus({hours: 9}).toISODate()
            const endDate = DateTime.fromSQL(raw.noticeEndDatetime).plus({hours: 9}).toISODate()
            const startTime = DateTime.fromSQL(raw.noticeStartDatetime).plus({hours: 9}).toFormat('HH:mm')
            const endTime = DateTime.fromSQL(raw.noticeEndDatetime).plus({hours: 9}).toFormat('HH:mm')
            this.startDate = startDate
            this.startTime = startTime
            this.endDate = endDate
            this.endTime = endTime
        }
    },
    data() {
        return {
            noticeTypeOptions: [],
            noticeDetailOptions: [],
            max: null,
            min: null,
            startDate: null,
            startTime: null,
            endDate: null,
            endTime: null,
            boardId: null,
            countryCode: null,
            boardTypeId: null,
            noticeDetailYn: null,
            title: '',
            content: '',
            contentPlain: '',
            tagItems: [],
            tagInput: '',
            options: {
                modules: {
                    toolbar: {
                        container: [
                            ['bold', 'italic', 'underline', 'strike'],
                            ['blockquote', 'code-block'],
                            [{ 'header': 1 }, { 'header': 2 }],
                            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                            [{ 'script': 'sub'}, { 'script': 'super' }],
                            [{ 'indent': '-1'}, { 'indent': '+1' }],
                            [{ 'direction': 'rtl' }],
                            [{ 'size': ['small', false, 'large', 'huge'] }],
                            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                            [{ 'color': [] }, { 'background': [] }],
                            [{ 'font': [] }],
                            [{ 'align': [] }],
                            ['clean'],
                            ['image'],
                        ],
                    },
                    imageDrop: true,
                    imageResize: {
                        modules: [ 'Resize', 'DisplaySize' ]
                    },
                }
            },
        }
    },
    methods: {
        onFilterNoticeTypeChanged(boardTypeId) {
            this.boardTypeId = boardTypeId
        },
        onFilterCountryCodesChanged(countryCode) {
            this.countryCode = countryCode
        },
        onFilterNoticeDetailChanged(noticeDetailYn) {
            this.noticeDetailYn = noticeDetailYn
        },
        async onDeleteClick() {
            if (!this.boardId) {
                return;
            }
            if (!confirm(this.t('삭제하시겠습니까?'))) {
                return;
            }
            try {
                await this.$request(PATH.BOARD.NOTICE.DELETE)
                    .setObject({ boardId: this.boardId })
                    .enqueue();
                alert(this.t('삭제 되었습니다.'));
                this.$router.push({ name: 'managertnbox.board.faq.list' });
            } catch (error) {
                console.error(error);
            }
        },
        onSaveClick() {
            // 국가 코드 설정 체크
            const countryCode = this.countryCode
            if (countryCode === null || countryCode === undefined) {
              alert(this.t('국가코드를 선택해주세요.'))
              return
            }
            // 공지사항 타입 설정 체크
            const boardTypeId = this.boardTypeId
            if (boardTypeId === null || boardTypeId === undefined) {
                alert(this.t('FAQ 타입을 선택해주세요.'))
                return
            }

            // 제목 체크
            const title = this.title.trim()
            if (!title) {
                alert(this.t("제목을 입력하여 주세요."))
                this.$refs.title.focus()
                return
            }
            // 내용 체크
            const content = this.content
            if (!content) {
                alert(this.t("내용을 입력하여 주세요."))
                return
            }
            // 저장 및 수정
            if (confirm(this.t('이대로 저장하시겠습니까?'))) {
                this.requestNoticeUpdate({
                    boardTypeId,
                    title,
                    content,
                    contentPlain: this.contentPlain,
                    tags: this.tagItems.join(','),
                })
            }
        },
        commitTagInput() {
            const raw = (this.tagInput || '').trim()
            if (!raw) return
            const tag = raw.replace(/^#/, '').trim()
            if (!tag) {
                this.tagInput = ''
                return
            }
            if (!this.tagItems.includes(tag)) {
                this.tagItems = [...this.tagItems, tag].slice(0, 20)
            }
            this.tagInput = ''
        },
        removeTag(tag) {
            this.tagItems = this.tagItems.filter(t => t !== tag)
        },
        parseTagNames(tags) {
            return (tags || '')
                .split(/[,\n\r]+/)
                .map(v => v.trim().replace(/^#/, '').trim())
                .filter(v => v.length > 0)
                .slice(0, 20)
        },
        async requestNoticeUpdate(data) {
            try {
                const displayName = this.$userManager.user.displayName
                console.log("#DISPLAY NAME : ", displayName)
                // TODO : 수정 API 추가 필요
                await this.$request(this.boardId ? PATH.BOARD.NOTICE.UPDATE : PATH.BOARD.NOTICE.ADD)
                    .setObject({
                        boardId: this.boardId,
                        countryCode: this.countryCode,
                        boardTypeId: data.boardTypeId,
                        noticeDetailYn: data.noticeDetailYn,
                        title: data.title,
                        content: data.content,
                        contentPlain: data.contentPlain,
                        tags: data.tags,
                        tagNames: this.parseTagNames(data.tags),
                        regUserId: displayName,
                        uptUserId: displayName,
                        noticeStartDatetime: data.startDateTime,
                        noticeEndDatetime: data.endDateTime,
                        useYn: 'Y',
                    })
                    .enqueue()
                alert(this.t("게시물이 저장 되었습니다."))
                this.$router.push({name: "managertnbox.board.faq.list"})
            } catch (error) {
                console.error(error)
            }
        }
    }
}
</script>

<style scoped>
.faq-edit-field-tight {
  margin-top: 8px !important;
}

.faq-edit-tags {
  margin-top: 6px !important;
}

.faq-edit-body-split {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  height: calc(100vh - 350px);
  min-height: 600px;
}

.faq-edit-body-pane {
  flex: 1 1 0;
  min-height: 0;
}

.faq-edit-quill {
  height: 100%;
  background-color: #ffffff;
}

.faq-edit-body-pane ::v-deep .ql-container {
  height: calc(100% - 42px);
}

.faq-edit-plain-textarea {
  height: 100%;
}

.faq-edit-plain-textarea ::v-deep .v-input__control,
.faq-edit-plain-textarea ::v-deep .v-input__slot {
  height: 100%;
}

.faq-edit-plain-textarea ::v-deep textarea {
  height: 100% !important;
  min-height: 100% !important;
}
</style>
